import type { IDemoAdminShowcase } from '@/interfaces/demoAdmin';

type DemoShowcaseFollowup = {
  label: string;
  livePrompt: string;
};

interface DemoShowcaseScenario {
  prompt: string;
  responseTitle: string;
  response: string;
  followups: DemoShowcaseFollowup[];
}

interface DemoShowcaseBusinessScenario extends DemoShowcaseScenario {
  stats: { value: string; label: string }[];
  chartLabel: string;
  chartNote: string;
  chart: { value: number; label: string }[];
}

interface DemoShowcaseTableScenario extends DemoShowcaseScenario {
  tableSummary: string;
  tableColumns: string[];
  tableRows: string[][];
}

interface DemoShowcaseStaffScenario extends DemoShowcaseScenario {
  instructorName: string;
  locationName: string;
  appointmentOpen: boolean;
  connectedCount: number;
  steps: {
    number: string;
    title: string;
    detail: string;
    duration: string;
    price: string;
  }[];
}

type DemoAdminShowcaseScenarios = {
  business: DemoShowcaseBusinessScenario;
  table: DemoShowcaseTableScenario;
  staff: DemoShowcaseStaffScenario;
};

function personName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

function formatNumber(value: number, language: 'en' | 'zh'): string {
  return new Intl.NumberFormat(language === 'zh' ? 'zh-TW' : 'en').format(
    value
  );
}

function addDays(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

function waitingCopy(
  language: 'en' | 'zh',
  followups: DemoShowcaseFollowup[]
): DemoShowcaseScenario {
  return language === 'zh'
    ? {
        prompt: '目前商家有哪些需要注意的營運狀況？',
        responseTitle: '正在載入 Demoapt 的即時資料…',
        response: '資料載入後，這裡會顯示與互動式 AI 相同的商家紀錄。',
        followups,
      }
    : {
        prompt: 'What needs attention in the business right now?',
        responseTitle: 'Loading live Demoapt data…',
        response:
          'This example will use the same business records as the interactive AI.',
        followups,
      };
}

export function buildDemoAdminShowcaseScenarios(
  language: 'en' | 'zh',
  showcase: IDemoAdminShowcase | null
): DemoAdminShowcaseScenarios {
  const businessFollowups: DemoShowcaseFollowup[] =
    language === 'zh'
      ? [
          {
            label: '查看重複未出席顧客',
            livePrompt: showcase
              ? `哪些顧客在 ${showcase.no_show_period.date_from} 至 ${showcase.no_show_period.date_to} 期間至少有兩次未出席？請列出每位顧客在同一期間的未出席次數和預約總數。`
              : '哪些顧客在過去 7 天內至少有兩次未出席？請列出每位顧客的未出席次數和預約總數。',
          },
          {
            label: '分析取消預約',
            livePrompt: showcase
              ? `${showcase.cancellation_period.date_from} 至 ${showcase.cancellation_period.date_to} 期間，哪些課程的顧客取消預約最多？請列出每個課程的取消次數和逾時或扣點取消次數。`
              : '過去 30 天哪些課程的顧客取消預約最多？請列出每個課程的取消次數和逾時或扣點取消次數。',
          },
          {
            label: '查看額滿課程',
            livePrompt: showcase
              ? `${showcase.upcoming_period.date_from} 至 ${showcase.upcoming_period.date_to} 這 14 個日期中，有哪些課程場次已額滿？請列出日期、時間、名額、已預約人數和候補人數。`
              : '未來 14 天有哪些課程場次已額滿？請列出日期、時間、名額、已預約人數和候補人數。',
          },
        ]
      : [
          {
            label: 'Show repeat no-shows',
            livePrompt: showcase
              ? `Which customers had at least two no-shows from ${showcase.no_show_period.date_from} through ${showcase.no_show_period.date_to}? Show each customer's no-show and reservation counts from that same period.`
              : 'Which customers have at least two no-shows in the last 7 days? Show the no-show and reservation counts for each customer.',
          },
          {
            label: 'Break down cancellations',
            livePrompt: showcase
              ? `Which classes had the most customer cancellations from ${showcase.cancellation_period.date_from} through ${showcase.cancellation_period.date_to}? Show each class's cancellation and late or penalty counts.`
              : 'Which classes had the most customer cancellations in the last 30 days? Show the cancellation and late or penalty counts for each class.',
          },
          {
            label: 'Show full classes',
            livePrompt: showcase
              ? `Which class sessions are full across the 14 dates from ${showcase.upcoming_period.date_from} through ${showcase.upcoming_period.date_to}? Show the date, time, capacity, booked count, and waiting-list count.`
              : 'Which class sessions are full over the next 14 days? Show the date, time, capacity, booked count, and waiting-list count.',
          },
        ];

  const tableFollowups: DemoShowcaseFollowup[] =
    language === 'zh'
      ? [
          {
            label: '應該先聯絡誰？',
            livePrompt: showcase
              ? `在 ${showcase.no_show_period.date_from} 至 ${showcase.no_show_period.date_to} 期間，哪位重複未出席顧客的未出席率最高？請用同一期間的未出席次數除以預約總數，顯示每位顧客的計算結果並指出最高者。`
              : '哪位重複未出席顧客的未出席率最高？請用未出席次數除以預約總數，顯示每位顧客的計算結果並指出最高者。',
          },
          {
            label: '查看預約紀錄',
            livePrompt: showcase
              ? `使用表格找出 ${showcase.no_show_period.date_from} 至 ${showcase.no_show_period.date_to} 期間未出席率最高的顧客，然後整理該顧客最近的有效預約紀錄，並清楚標示已出席與未出席。`
              : '使用表格找出未出席率最高的顧客，然後整理該顧客最近的有效預約紀錄，並清楚標示已出席與未出席。',
          },
          {
            label: '草擬關懷訊息',
            livePrompt: showcase
              ? `找出 ${showcase.no_show_period.date_from} 至 ${showcase.no_show_period.date_to} 期間未出席率最高的重複未出席顧客，並草擬一則簡短、友善且個人化的關懷訊息。不要傳送，只顯示草稿。`
              : '找出過去 7 天內未出席比例最高的重複未出席顧客，並草擬一則簡短、友善且個人化的關懷訊息。不要傳送，只顯示草稿。',
          },
        ]
      : [
          {
            label: 'Who should I contact first?',
            livePrompt: showcase
              ? `Which repeat no-show customer had the highest no-show rate from ${showcase.no_show_period.date_from} through ${showcase.no_show_period.date_to}? Calculate each rate as no-shows divided by reservations from that same period, show every result, and identify the highest.`
              : 'Which repeat no-show customer has the highest no-show rate? Calculate each rate as no-shows divided by reservations, show every result, and identify the highest.',
          },
          {
            label: 'Show booking history',
            livePrompt: showcase
              ? `Use the table to identify the customer with the highest no-show rate from ${showcase.no_show_period.date_from} through ${showcase.no_show_period.date_to}, then summarize that customer's recent active booking history and clearly label attended versus missed bookings.`
              : "Use the table to identify the customer with the highest no-show rate, then summarize that customer's recent active booking history and clearly label attended versus missed bookings.",
          },
          {
            label: 'Draft a check-in',
            livePrompt: showcase
              ? `Find the repeat no-show customer with the highest no-show rate from ${showcase.no_show_period.date_from} through ${showcase.no_show_period.date_to} and draft a short, friendly, personalized check-in. Do not send it; show the draft only.`
              : 'Find the repeat no-show customer with the highest no-show rate and draft a short, friendly, personalized check-in. Do not send it; show the draft only.',
          },
        ];

  if (!showcase) {
    const business = waitingCopy(language, businessFollowups);
    const table = waitingCopy(language, tableFollowups);
    const staff = waitingCopy(language, []);
    return {
      business: {
        ...business,
        stats: [
          { value: '—', label: language === 'zh' ? '未出席' : 'No-shows' },
          { value: '—', label: language === 'zh' ? '取消' : 'Cancellations' },
          { value: '—', label: language === 'zh' ? '候補' : 'Waiting' },
        ],
        chartLabel:
          language === 'zh'
            ? '目前需要注意的訊號'
            : 'Current attention signals',
        chartNote:
          language === 'zh' ? '即時 Demoapt 資料' : 'Live Demoapt data',
        chart: [],
      },
      table: {
        ...table,
        tableSummary:
          language === 'zh' ? '正在載入顧客紀錄' : 'Loading customer records',
        tableColumns:
          language === 'zh'
            ? ['顧客', '未出席', '預約', '未出席率']
            : ['Customer', 'No-shows', 'Reservations', 'No-show rate'],
        tableRows: [],
      },
      staff: {
        ...staff,
        instructorName: language === 'zh' ? '正在載入員工' : 'Loading staff',
        locationName: 'Demoapt',
        appointmentOpen: false,
        connectedCount: 0,
        steps: [],
      },
    };
  }

  const repeatCustomers = showcase.repeat_no_show_customers;
  const repeatNoShows = repeatCustomers.reduce(
    (total, customer) => total + customer.customer_no_shows,
    0
  );
  const instructor = showcase.featured_instructor;
  const instructorName = personName(
    instructor.customer_first_name,
    instructor.customer_last_name
  );
  const locationName = instructor.location_name || 'Demoapt';
  const appointmentClasses = instructor.connected_classes.filter(
    item => item.class_appointment_open === 1
  );
  const featuredClasses = appointmentClasses.slice(0, 3);
  const availabilityClass = appointmentClasses[0];
  const availabilityServiceCn =
    availabilityClass?.class_name_cn || availabilityClass?.class_name_en || '';
  const availabilityServiceEn = availabilityClass?.class_name_en || '';
  const currency = instructor.settings_currency || 'TWD';

  const staffFollowups: DemoShowcaseFollowup[] =
    language === 'zh'
      ? [
          {
            label: '比較顯示的服務',
            livePrompt: `比較上方顯示的三項 ${instructorName} 服務之時間長度與價格。哪一項時間最短，哪一項價格最低？`,
          },
          {
            label: '查看明日可預約時段',
            livePrompt: `查看 ${instructorName} 在 ${addDays(showcase.upcoming_period.date_from, 1)}${availabilityServiceCn ? ` 提供「${availabilityServiceCn}」` : ''} 的即時可預約時段。請列出可預約開始時間；如果沒有時段，請說明原因。`,
          },
          {
            label: '查看教練名單',
            livePrompt:
              '列出示範商家的所有現職教練，以及每位教練未來 30 天的排課堂數。',
          },
        ]
      : [
          {
            label: 'Compare shown services',
            livePrompt: `Compare the duration and price of the three ${instructorName} services shown above. Which is shortest, and which costs the least?`,
          },
          {
            label: "Check tomorrow's availability",
            livePrompt: `Check ${instructorName}'s live appointment availability on ${addDays(showcase.upcoming_period.date_from, 1)}${availabilityServiceEn ? ` for ${availabilityServiceEn}` : ''}. Show available start times, or explain why none are available.`,
          },
          {
            label: 'View instructor roster',
            livePrompt:
              "List all active instructors in the Demo business and show each instructor's scheduled sessions over the next 30 days.",
          },
        ];

  if (language === 'zh') {
    return {
      business: {
        prompt: '目前商家有哪些需要注意的營運狀況？',
        responseTitle: `${formatNumber(showcase.customer_no_shows, language)} 次未出席最值得注意，另有 ${formatNumber(showcase.customers_on_waiting_list, language)} 人正在候補`,
        response: `過去 7 天共有 ${formatNumber(showcase.customer_no_shows, language)} 次未出席，其中 ${formatNumber(repeatCustomers.length, language)} 位顧客重複未出席。過去 30 天另有 ${formatNumber(showcase.customer_cancellations, language)} 次顧客取消，其中 ${formatNumber(showcase.customer_cancellation_penalties, language)} 次為逾時或扣點取消；未來 14 天有 ${formatNumber(showcase.full_class_schedules, language)} 堂課已額滿，共 ${formatNumber(showcase.customers_on_waiting_list, language)} 人候補。`,
        stats: [
          {
            value: formatNumber(showcase.customer_no_shows, language),
            label: '7 天內未出席',
          },
          {
            value: formatNumber(showcase.customer_cancellations, language),
            label: '30 天內取消',
          },
          {
            value: formatNumber(showcase.customers_on_waiting_list, language),
            label: '未來 14 天候補',
          },
        ],
        chartLabel: '目前需要注意的訊號',
        chartNote: '即時 Demoapt 資料',
        chart: [
          { value: showcase.customer_no_shows, label: '未出席' },
          { value: showcase.customer_cancellations, label: '取消' },
          {
            value: showcase.customer_cancellation_penalties,
            label: '逾時取消',
          },
          { value: showcase.customers_on_waiting_list, label: '候補' },
        ],
        followups: businessFollowups,
      },
      table: {
        prompt: '哪些顧客因重複未出席而需要跟進？',
        responseTitle: `${formatNumber(repeatCustomers.length, language)} 位顧客共造成 ${formatNumber(repeatNoShows, language)} 次未出席`,
        response:
          '這些是過去 7 天內至少未出席兩次的顧客。表格直接使用 Demoapt 的預約與出席紀錄，未出席率是以同一期間的預約總數計算。',
        tableSummary: `${formatNumber(repeatCustomers.length, language)} 位顧客 · 7 天內 ${formatNumber(repeatNoShows, language)} 次未出席`,
        tableColumns: ['顧客', '未出席', '預約', '未出席率'],
        tableRows: repeatCustomers.map(customer => [
          `${personName(customer.customer_first_name, customer.customer_last_name)}|顧客 #${customer.customer_id}`,
          formatNumber(customer.customer_no_shows, language),
          formatNumber(customer.customer_reservations, language),
          `${Math.round((customer.customer_no_shows / customer.customer_reservations) * 100)}%`,
        ]),
        followups: tableFollowups,
      },
      staff: {
        prompt: `${instructorName} 在 ${locationName} 可以提供哪些服務？`,
        responseTitle: `${instructorName} 已可接受預約，並連結 ${formatNumber(instructor.connected_classes.length, language)} 項服務`,
        response: `她在 ${locationName} 的預約功能目前為${instructor.customer_instructor_appointment_open === 1 ? '開啟' : '關閉'}，並已連結 ${formatNumber(instructor.connected_classes.length, language)} 項實際服務。以下顯示其中三項；互動式 AI 可以查詢完整清單與即時可預約時段。`,
        instructorName,
        locationName,
        appointmentOpen: instructor.customer_instructor_appointment_open === 1,
        connectedCount: instructor.connected_classes.length,
        steps: featuredClasses.map((item, index) => ({
          number: String(index + 1),
          title:
            item.class_name_cn || item.class_name_en || `服務 ${index + 1}`,
          detail: `${item.class_duration} 分鐘 · ${currency} ${formatNumber(item.class_price, language)}`,
          duration: `${item.class_duration} 分鐘`,
          price: `${currency} ${formatNumber(item.class_price, language)}`,
        })),
        followups: staffFollowups,
      },
    };
  }

  return {
    business: {
      prompt: 'What needs attention in the business right now?',
      responseTitle: `${formatNumber(showcase.customer_no_shows, language)} no-shows stand out—and ${formatNumber(showcase.customers_on_waiting_list, language)} customers are waiting`,
      response: `There were ${formatNumber(showcase.customer_no_shows, language)} no-shows in the last 7 days, with ${formatNumber(repeatCustomers.length, language)} customers missing more than once. The last 30 days also had ${formatNumber(showcase.customer_cancellations, language)} customer cancellations (${formatNumber(showcase.customer_cancellation_penalties, language)} late or penalized), while ${formatNumber(showcase.full_class_schedules, language)} sessions are full over the next 14 days with ${formatNumber(showcase.customers_on_waiting_list, language)} people waiting.`,
      stats: [
        {
          value: formatNumber(showcase.customer_no_shows, language),
          label: 'No-shows · 7 days',
        },
        {
          value: formatNumber(showcase.customer_cancellations, language),
          label: 'Cancellations · 30 days',
        },
        {
          value: formatNumber(showcase.customers_on_waiting_list, language),
          label: 'Waiting · next 14 days',
        },
      ],
      chartLabel: 'Current attention signals',
      chartNote: 'Live Demoapt data',
      chart: [
        { value: showcase.customer_no_shows, label: 'No-shows' },
        { value: showcase.customer_cancellations, label: 'Cancelled' },
        { value: showcase.customer_cancellation_penalties, label: 'Late' },
        { value: showcase.customers_on_waiting_list, label: 'Waiting' },
      ],
      followups: businessFollowups,
    },
    table: {
      prompt: 'Which customers need a follow-up for repeated no-shows?',
      responseTitle: `${formatNumber(repeatCustomers.length, language)} customers account for ${formatNumber(repeatNoShows, language)} no-shows`,
      response:
        "These customers each missed at least two bookings in the last 7 days. The table comes directly from Demoapt attendance and reservation records; the rate uses each customer's bookings in that same period.",
      tableSummary: `${formatNumber(repeatCustomers.length, language)} customers · ${formatNumber(repeatNoShows, language)} no-shows in 7 days`,
      tableColumns: ['Customer', 'No-shows', 'Reservations', 'No-show rate'],
      tableRows: repeatCustomers.map(customer => [
        `${personName(customer.customer_first_name, customer.customer_last_name)}|Customer #${customer.customer_id}`,
        formatNumber(customer.customer_no_shows, language),
        formatNumber(customer.customer_reservations, language),
        `${Math.round((customer.customer_no_shows / customer.customer_reservations) * 100)}%`,
      ]),
      followups: tableFollowups,
    },
    staff: {
      prompt: `What can ${instructorName} offer at ${locationName}?`,
      responseTitle: `${instructorName} is appointment-ready across ${formatNumber(instructor.connected_classes.length, language)} services`,
      response: `Appointment booking is currently ${instructor.customer_instructor_appointment_open === 1 ? 'enabled' : 'disabled'} for ${instructorName} at ${locationName}, and ${instructor.connected_classes.length} real services are connected. Here are three; the interactive AI can inspect the full list and live availability.`,
      instructorName,
      locationName,
      appointmentOpen: instructor.customer_instructor_appointment_open === 1,
      connectedCount: instructor.connected_classes.length,
      steps: featuredClasses.map((item, index) => ({
        number: String(index + 1),
        title:
          item.class_name_en || item.class_name_cn || `Service ${index + 1}`,
        detail: `${item.class_duration} min · ${currency} ${formatNumber(item.class_price, language)}`,
        duration: `${item.class_duration} min`,
        price: `${currency} ${formatNumber(item.class_price, language)}`,
      })),
      followups: staffFollowups,
    },
  };
}
